import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

@Component({
    selector: 'sn-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.css']
})
export class DropdownComponent implements OnInit {
    @Input() options:any[];
    @Input() empty_selection_label:string;
    @Input() style_class:string;
    @Input() initial_selection:any;
    @Input() combo_settings:any;
    @ViewChild('labelEl') labelEl: ElementRef;
    @ViewChild('comboEl') comboEl: ElementRef;
    @ViewChild('listboxEl') listboxEl: ElementRef;
    @Output() public onOptionSelected = new EventEmitter();
  
    public activeIndex: number = 0;
    public open: boolean = false;
    public searchString: string = '';
    public searchTimeout = null;
    public selected_option: any = null;
    public classList: string[] = ['combo', 'js-select'];

    constructor() { }

    ngOnInit() {
        if (this.initial_selection != null) {
            var option = this.options.find(o => o.label == this.initial_selection.label);
            this.onOptionClick(null, option);
        }
    }

    //#region angular adapted functions
    public getStyleClass() {
        return this.style_class ? this.style_class : 'sn';
    }

    public getComboClass() {
        return this.classList.join(' ');
    }

    public getOptions() {
        return this.options;
    }

    public clearSelection() {
        this.selected_option = null;
    }

    getOptionClass(option) {
        var index = this.options.indexOf(option);
        var option_class = this.selected_option == option ? 'combo-option option-current' : 'combo-option';
        return (index > 0) ? option_class + ' combo-separator' : option_class;
    }

    getSelectedOptionLabel() {
        return (this.selected_option) ? this.selected_option.label : this.empty_selection_label;
    }

    getAriaSelected(index) {
        return index == 0;
    }

    getAriaActiveDescendant() {
        if (this.open) {
            var index = this.options.indexOf(this.selected_option);
            return `combo-${index}`;
        }
        return null;
    }

    getAriaExpanded() {
        return this.open;
    }

    isOptionSelected(option) {
        return this.selected_option == option;
    }

    cbGetAriaLabel() {
        return this.combo_settings && this.combo_settings.aria_label;
    }

    cbGetAriaLabelledby() {
        var has_aria_label = this.combo_settings && this.combo_settings.aria_label;
        return has_aria_label ? "" : "combo1-label";
    }
    //#endregion

    // Save a list of named combobox actions, for future readability
    public SelectActions: any = {
        Close: 0,
        CloseSelect: 1,
        First: 2,
        Last: 3,
        Next: 4,
        Open: 5,
        PageDown: 6,
        PageUp: 7,
        Previous: 8,
        Select: 9,
        Type: 10,
    };

    // filter an array of options against an input string
    // returns an array of options that begin with the filter string, case-independent
    filterOptions(options = [], filter, exclude = []) {
        return options.filter((option) => {
            const matches = option.toLowerCase().indexOf(filter.toLowerCase()) === 0;
            return matches && exclude.indexOf(option) < 0;
        });
    }

    // map a key press to an action
    getActionFromKey(event, menuOpen) {
        const { key, altKey, ctrlKey, metaKey } = event;
        const openKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' ']; // all keys that will do the default open action
        // handle opening when closed
        if (!menuOpen && openKeys.includes(key)) {
            return this.SelectActions.Open;
        }

        // home and end move the selected option when open or closed
        if (key === 'Home') {
            return this.SelectActions.First;
        }
        if (key === 'End') {
            return this.SelectActions.Last;
        }

        // handle typing characters when open or closed
        if (
            key === 'Backspace' ||
            key === 'Clear' ||
            (key.length === 1 && key !== ' ' && !altKey && !ctrlKey && !metaKey)
        ) {
            return this.SelectActions.Type;
        }

        // handle keys when open
        if (menuOpen) {
            if (key === 'ArrowUp' && altKey) {
                return this.SelectActions.CloseSelect;
            } else if (key === 'ArrowDown' && !altKey) {
                return this.SelectActions.Next;
            } else if (key === 'ArrowUp') {
                return this.SelectActions.Previous;
            } else if (key === 'PageUp') {
                return this.SelectActions.PageUp;
            } else if (key === 'PageDown') {
                return this.SelectActions.PageDown;
            } else if (key === 'Escape') {
                return this.SelectActions.Close;
            } else if (key === 'Enter' || key === ' ') {
                return this.SelectActions.CloseSelect;
            }
        }
    }

    // return the index of an option from an array of options, based on a search string
    // if the filter is multiple iterations of the same letter (e.g "aaa"), then cycle through first-letter matches
    getIndexByLetter(options, filter, startIndex = 0) {
        const orderedOptions = [
            ...options.slice(startIndex),
            ...options.slice(0, startIndex),
        ];
        const firstMatch = this.filterOptions(orderedOptions, filter)[0];
        const allSameLetter = (array) => array.every((letter) => letter === array[0]);

        // first check if there is an exact match for the typed string
        if (firstMatch) {
            return options.indexOf(firstMatch);
        }

        // if the same letter is being repeated, cycle through first-letter matches
        else if (allSameLetter(filter.split(''))) {
            const matches = this.filterOptions(orderedOptions, filter[0]);
            return options.indexOf(matches[0]);
        }

        // if no matches, return -1
        else {
            return -1;
        }
    }

    // get an updated option index after performing an action
    getUpdatedIndex(currentIndex, maxIndex, action) {
        const pageSize = 10; // used for pageup/pagedown

        switch (action) {
            case this.SelectActions.First:
                return 0;
            case this.SelectActions.Last:
                return maxIndex;
            case this.SelectActions.Previous:
                return Math.max(0, currentIndex - 1);
            case this.SelectActions.Next:
                return Math.min(maxIndex, currentIndex + 1);
            case this.SelectActions.PageUp:
                return Math.max(0, currentIndex - pageSize);
            case this.SelectActions.PageDown:
                return Math.min(maxIndex, currentIndex + pageSize);
            default:
                return currentIndex;
        }
    }

    isElementInView(element) {
        var bounding = element.getBoundingClientRect();

        return (
            bounding.top >= 0 &&
            bounding.left >= 0 &&
            bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    }

    isScrollable(element) {
        return element && element.clientHeight < element.scrollHeight;
    }

    maintainScrollVisibility(activeElement, scrollParent) {
        const { offsetHeight, offsetTop } = activeElement;
        const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent;

        const isAbove = offsetTop < scrollTop;
        const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;

        if (isAbove) {
            scrollParent.scrollTo(0, offsetTop);
        } else if (isBelow) {
            scrollParent.scrollTo(0, offsetTop - parentOffsetHeight + offsetHeight);
        }
    }

    /*
     * Select Component
     * Accepts a combobox element and an array of string options
     */
    // const Select = function (el, options = []) {
    //     // element refs
    //     this.el = el;
    //     this.labelEl = el.querySelector('.combo-label');
    //     this.comboEl = el.querySelector('[role=combobox]');
    //     this.listboxEl = el.querySelector('[role=listbox]');

    //     // data
    //     this.idBase = this.comboEl.id || 'combo';
    //     this.options = options;

    //     // state
    //     this.activeIndex = 0;
    //     this.open = false;
    //     this.searchString = '';
    //     this.searchTimeout = null;

    //     // init
    //     if (el && this.comboEl && this.listboxEl) {
    //       this.init();
    //     }
    //   };

    //   Select.prototype.init = function () {
    //     // select first option by default
    //     this.comboEl.innerHTML = this.options[0];

    //     // add event listeners
    //     this.labelEl.addEventListener('click', this.onLabelClick.bind(this));
    //     this.comboEl.addEventListener('blur', this.onComboBlur.bind(this));
    //     this.listboxEl.addEventListener('focusout', this.onComboBlur.bind(this));
    //     this.comboEl.addEventListener('click', this.onComboClick.bind(this));
    //     this.comboEl.addEventListener('keydown', this.onComboKeyDown.bind(this));

    //     // create options
    //     this.options.map((option, index) => {
    //       const optionEl = this.createOption(option, index);
    //       this.listboxEl.appendChild(optionEl);
    //     });
    //   };    

    // Select.prototype.createOption = function (optionText, index) {
    //     const optionEl = document.createElement('div');
    //     optionEl.setAttribute('role', 'option');
    //     optionEl.id = `${this.idBase}-${index}`;
    //     optionEl.className =
    //       index === 0 ? 'combo-option option-current' : 'combo-option';
    //     optionEl.setAttribute('aria-selected', `${index === 0}`);
    //     optionEl.innerText = optionText;

    //     optionEl.addEventListener('click', (event) => {
    //       event.stopPropagation();
    //       this.onOptionClick(index);
    //     });
    //     optionEl.addEventListener('mousedown', this.onOptionMouseDown.bind(this));

    //     return optionEl;
    //   };

    getSearchString(char) {
        // reset typing timeout and start new timeout
        // this allows us to make multiple-letter matches, like a native select
        if (typeof this.searchTimeout === 'number') {
            window.clearTimeout(this.searchTimeout);
        }

        this.searchTimeout = window.setTimeout(() => {
            this.searchString = '';
        }, 500);

        // add most recent letter to saved search string
        this.searchString += char;
        return this.searchString;
    };

    onLabelClick(event) {
        this.comboEl.nativeElement.focus();
    }

    onComboBlur(event) {
        if (this.listboxEl && this.listboxEl.nativeElement.contains(event.relatedTarget)) {
            return;
        }

        // select current option and close
        if (this.open) {
            this.selectOption(this.activeIndex);
            this.updateMenuState(false, false);
        }
    }

    onComboClick(event) {
        this.updateMenuState(!this.open, false);
    }

    onComboKeyDown(event) {
        const { key } = event;
        const max = this.options.length - 1;

        const action = this.getActionFromKey(event, this.open);

        switch (action) {
            case this.SelectActions.Last:
            case this.SelectActions.First:
                this.updateMenuState(true);
            // intentional fallthrough
            case this.SelectActions.Next:
            case this.SelectActions.Previous:
            case this.SelectActions.PageUp:
            case this.SelectActions.PageDown:
                event.preventDefault();
                return this.onOptionChange(this.getUpdatedIndex(this.activeIndex, max, action));
            case this.SelectActions.CloseSelect:
                event.preventDefault();
                this.selectOption(this.activeIndex);
            // intentional fallthrough
            case this.SelectActions.Close:
                event.preventDefault();
                return this.updateMenuState(false);
            case this.SelectActions.Type:
                return this.onComboType(key);
            case this.SelectActions.Open:
                event.preventDefault();
                return this.updateMenuState(true);
        }
    }

    onComboType(letter) {
        // open the listbox if it is closed
        this.updateMenuState(true);

        // find the index of the first matching option
        const searchString = this.getSearchString(letter);
        const searchIndex = this.getIndexByLetter(this.options, searchString, this.activeIndex + 1);

        // if a match was found, go to it
        if (searchIndex >= 0) {
            this.onOptionChange(searchIndex);
        }
        // if no matches, clear the timeout and search string
        else {
            window.clearTimeout(this.searchTimeout);
            this.searchString = '';
        }
    };

    onOptionChange(index, option = null) {
        if (index < 0) return;
        this.activeIndex = index;
        if (option == null) option = this.options[index];
        this.selected_option = option;
        var option_id = 'combo-' + index;
        var option_element = document.getElementById(option_id);
        // ensure the new option is in view
        if (this.listboxEl && this.isScrollable(this.listboxEl.nativeElement)) {
            this.maintainScrollVisibility(option_element, this.listboxEl.nativeElement);
        }

        // ensure the new option is visible on screen
        // ensure the new option is in view
        if (option_element && !this.isElementInView(option_element)) {
            option_element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        if (this.onOptionSelected) this.onOptionSelected.emit(option);  // emit onOptionSelected event to update the parent with the seleced_option
    }

    onOptionClick(event, option) {
        this.selectOption(this.options.indexOf(option), option);
        this.onOptionChange(this.options.indexOf(option), option);
        this.updateMenuState(false);
    }

    onOptionMouseDown(event) {
        event.target.ignoreBlur = true;
    }

    selectOption(index, option = null) {
        if (option) {
            this.selected_option = option;
            this.activeIndex = this.options.indexOf(option);
        } else {
            this.activeIndex = index;
            this.selected_option = this.options[index];
        }
        if (this.onOptionSelected) this.onOptionSelected.emit(this.selected_option);  // emit onOptionSelected event to update the parent with the seleced_option
    };

    updateMenuState(open, callFocus = true) {
        if (this.open === open) {
            return;
        }

        // update state
        this.open = open;

        // update aria-expanded and styles
        // this.comboEl.nativeElement.setAttribute('aria-expanded', `${open}`);
        if (open) {
            this.classList.push('open');
        } else {
            var index = this.classList.indexOf('open');
            this.classList.splice(index, 1);
        }

        // update activedescendant
        //const activeID = open ? `${this.idBase}-${this.activeIndex}` : '';
        // this.comboEl.nativeElement.setAttribute('aria-activedescendant', activeID);

        if (!open && !this.isElementInView(this.comboEl.nativeElement)) {
            this.comboEl.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }

        // move focus back to the combobox, if needed
        callFocus && this.comboEl.nativeElement.focus();
    };
}



// WEBPACK FOOTER //
// C:/Azure/Source/Workspaces/SalesNOW/SalesSOLO/WebSites/SalesSOLO.Web.Angular.telesales/src/app/components/common/dropdown/dropdown.component.ts